import Decimal from 'decimal.js';

export type PayoutRow = {
  rank: string;
  from: number;
  to: number;
  amount: number;
  isTicket: boolean;
  bountyAmount?: number;
};

export function nonNegativeAmount(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
    return undefined;
  }
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : undefined;
}

// percentPrizePool is the server-calculated AMOUNT, not a percentage.
// Preserve ranges and ticket rewards from the same feed used by MTTs.
export function parsePayoutRows(rows: any[]): PayoutRow[] | null {
  const result: PayoutRow[] = [];
  for (const row of rows) {
    const match = String(row?.rank ?? '').match(/^(\d+)(?:\s*-\s*(\d+))?$/);
    const amount = nonNegativeAmount(row?.percentPrizePool);
    if (!match || amount === undefined) return null;
    const from = Number(match[1]);
    const to = Number(match[2] ?? match[1]);
    if (!Number.isSafeInteger(from) || !Number.isSafeInteger(to) || from < 1 || to < from) {
      return null;
    }
    result.push({
      rank: from === to ? String(from) : `${from}-${to}`,
      from,
      to,
      amount,
      isTicket: row.isTicket === true || Number(row.isTicket) === 1,
      bountyAmount: nonNegativeAmount(row.bountyAmount)
    });
  }
  result.sort((a, b) => a.from - b.from);
  if (result.some((row, i) => i > 0 && row.from <= result[i - 1].to)) return null;
  return result;
}

export function payoutPool(rows: PayoutRow[]): number | undefined {
  if (!rows.length || rows.some((row) => row.isTicket)) return undefined;
  return rows
    .reduce(
      (sum, row) => sum.add(new Decimal(row.amount).mul(row.to - row.from + 1)),
      new Decimal(0)
    )
    .toDecimalPlaces(2)
    .toNumber();
}

export function ordinal(position: number): string {
  const lastTwo = position % 100;
  const suffix =
    lastTwo >= 11 && lastTwo <= 13
      ? 'th'
      : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[position % 10] ?? 'th';
  return `${position}${suffix}`;
}
